/**
 * infiles-content.ts — innhold for /infiles (norsk + engelsk)
 *
 * inFiles er en filservice for chiptuning: forhandlere oppretter jobber for et
 * kjøretøy, laster opp original ECU-fil, verkstedet leverer en modifisert fil, og
 * jobben går gjennom statussteg (se src/sections/jobs/). Påstandene under kan
 * spores til koden — unntatt de som er merket «BEKREFT» nedenfor, som eier må
 * lese gjennom før publisering.
 *
 * Offentlig side: statisk innhold i frontend er tillatt. Ingen dynamiske
 * verdier (tall, status fra systemet) skal legges her.
 *
 * BEKREFT før publisering:
 * - persona «Verksted/tuner» (finnes den som egen rolle?) og «levering» av fil
 * - FAQ: støttede filtyper, og hvem som ser hvilke jobber/filer
 * - kontaktadressen (CONTACT) — admin@ntune.no er kun en plassholder
 */

import type { Locale } from '@/types/landing';
import type { InfilesContent } from '@/types/infiles';

/** TODO: bekreft riktig kontaktadresse for tilgangsforespørsler. */
const CONTACT = 'mailto:admin@ntune.no?subject=Tilgang%20til%20inFiles';
const LOGIN = '/login';

const no: InfilesContent = {
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  breadcrumb: {
    label: 'Brødsmulesti',
    links: [{ label: 'Hjem', href: '/' }],
    current: 'inFiles',
  },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'funksjoner', label: 'Funksjoner' },
      { id: 'steg', label: 'Slik fungerer det' },
      { id: 'for-hvem', label: 'For hvem' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Logg inn', href: LOGIN },
  },
  hero: {
    heading: 'inFiles',
    badge: 'Alpha',
    subheading:
      'Filservice for chiptuning: send original ECU-filer, følg jobben gjennom hvert steg og last ned den modifiserte filen, samlet i én arbeidsflyt.',
    primaryCta: { label: 'Logg inn', href: LOGIN },
    secondaryCta: { label: 'Dette er inFiles', href: '#oversikt' },
    illustrationLabel: 'Illustrasjon av original fil, ECU-brikke og modifisert fil',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'Fra original til modifisert fil. Uten e-posttråder.',
    body: [
      'inFiles samler hele tuningjobben på ett sted: kjøretøyet, original-filen, samtalen mellom forhandler og verksted, og den modifiserte filen som leveres tilbake.',
      'Hver jobb følger et fast forløp med statussteg, så alle parter alltid ser hvor jobben står.',
    ],
  },
  features: {
    eyebrow: 'Funksjoner',
    heading: 'Dette får du',
    intro: 'Alt som hører til en tuningjobb, samlet på samme sted.',
    panelLabel: 'Illustrasjon av original og modifisert fil',
    items: [
      {
        title: 'Jobber og status',
        body: 'Opprett en jobb med tuningtype, prioritet, girkasse og verktøy, og følg den gjennom statusstegene til den er ferdig.',
      },
      {
        title: 'Original og modifisert fil',
        body: 'Last opp original-filen med ECU-maskinvare og -programvare. Den modifiserte filen lagres på samme jobb, med checksum og filstørrelse. Opptil 50 MB per fil.',
      },
      {
        title: 'Kjøretøyoppslag',
        body: 'Slå opp kjøretøyet mot Statens vegvesen, så kjennemerke, understellsnummer og tekniske data følger jobben.',
      },
      {
        title: 'Chat på hver jobb',
        body: 'Forhandler og verksted snakker sammen direkte på jobben, i stedet for i e-post.',
      },
      {
        title: 'Varsler i sanntid',
        body: 'Endringer på jobbene dukker opp som varsler mens du jobber.',
      },
      {
        title: 'Forhandlere og roller',
        body: 'Tilgangen styres per forhandler og rolle, så hver bruker ser det som er relevant for dem.',
      },
    ],
  },
  promo: {
    title: 'inFiles er i alpha',
    body: 'Modulen brukes foreløpig internt for testing. Ta kontakt for å få tilgang.',
    cta: { label: 'Be om tilgang', href: CONTACT },
  },
  steps: {
    eyebrow: 'Slik fungerer det',
    heading: 'Fra jobb til ferdig fil',
    intro: 'Fire steg fra bestilling til levering.',
    panelLabel: 'Illustrasjon av original fil, ECU-brikke og modifisert fil',
    items: [
      { title: 'Opprett jobben', body: 'Velg kjøretøy (slå det opp mot Statens vegvesen), tuningtype og prioritet.' },
      { title: 'Last opp original-filen', body: 'Legg ved ECU-filen fra kjøretøyet, og oppgi maskinvare og programvare.' },
      { title: 'Verkstedet behandler jobben', body: 'Følg statusen, og snakk med verkstedet i chatten på jobben.' },
      { title: 'Last ned den modifiserte filen', body: 'Når jobben er ferdig, ligger den modifiserte filen på jobben.' },
    ],
  },
  audiences: {
    eyebrow: 'For hvem',
    heading: 'Bygget for hele tuningkjeden',
    items: [
      {
        icon: 'dealer',
        title: 'Forhandler',
        body: 'Opprett jobber og last opp filer for kundenes biler, og følg hver jobb til den er ferdig.',
        link: { label: 'Logg inn', href: LOGIN },
      },
      {
        icon: 'workshop',
        title: 'Verksted',
        body: 'Se innkomne jobber med kjøretøy, filer og kommentarer, og lever modifiserte filer tilbake.',
        link: { label: 'Logg inn', href: LOGIN },
      },
      {
        icon: 'admin',
        title: 'Administrator',
        body: 'Styr brukere, roller og forhandlere, og hold oversikt over alle jobber.',
        link: { label: 'Be om tilgang', href: CONTACT },
      },
    ],
  },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      {
        question: 'Hvem kan bruke inFiles?',
        answer: 'inFiles er i alpha og brukes foreløpig internt for testing. Ta kontakt for å få tilgang.',
      },
      { question: 'Hvor store filer kan jeg laste opp?', answer: 'Hver fil kan være opptil 50 MB.' },
      {
        question: 'Hvilke filtyper støttes?',
        answer: 'Original- og modifiserte filer lastes opp som vanlige filer på jobben. Ta kontakt for detaljer om støttede formater.',
      },
      {
        question: 'Hvem kan se filene mine?',
        answer: 'Tilgangen styres per forhandler og rolle. Brukere ser jobber og filer som hører til deres forhandler.',
      },
    ],
  },
  cta: {
    heading: 'Klar til å logge inn?',
    body: 'Logg inn for å åpne inFiles, eller ta kontakt for å få tilgang.',
    primary: { label: 'Logg inn', href: LOGIN },
  },
  footer: {
    tagline: 'inFiles — filservice for chiptuning, en modul i inSuite.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inFiles',
  },
};

const en: InfilesContent = {
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  breadcrumb: {
    label: 'Breadcrumb',
    links: [{ label: 'Home', href: '/' }],
    current: 'inFiles',
  },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'funksjoner', label: 'Features' },
      { id: 'steg', label: 'How it works' },
      { id: 'for-hvem', label: 'Who it is for' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'Log in', href: LOGIN },
  },
  hero: {
    heading: 'inFiles',
    badge: 'Alpha',
    subheading:
      'A file service for chip tuning: send original ECU files, follow each job through every step and download the modified file, all in one workflow.',
    primaryCta: { label: 'Log in', href: LOGIN },
    secondaryCta: { label: 'This is inFiles', href: '#oversikt' },
    illustrationLabel: 'Illustration of an original file, an ECU chip and a modified file',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'From original to modified file. No email threads.',
    body: [
      'inFiles keeps the whole tuning job in one place: the vehicle, the original file, the conversation between dealer and workshop, and the modified file that is delivered back.',
      'Every job follows a fixed flow of status steps, so everyone always sees where the job stands.',
    ],
  },
  features: {
    eyebrow: 'Features',
    heading: 'What you get',
    intro: 'Everything that belongs to a tuning job, in one place.',
    panelLabel: 'Illustration of an original and a modified file',
    items: [
      {
        title: 'Jobs and status',
        body: 'Create a job with tuning type, priority, gearbox and tool, and follow it through the status steps until it is done.',
      },
      {
        title: 'Original and modified file',
        body: 'Upload the original file with ECU hardware and software. The modified file is stored on the same job, with checksum and file size. Up to 50 MB per file.',
      },
      {
        title: 'Vehicle lookup',
        body: 'Look the vehicle up with the Norwegian Public Roads Administration, so registration number, chassis number and technical data follow the job.',
      },
      {
        title: 'Chat on every job',
        body: 'Dealer and workshop talk directly on the job, instead of over email.',
      },
      {
        title: 'Real-time notifications',
        body: 'Changes to jobs show up as notifications while you work.',
      },
      {
        title: 'Dealers and roles',
        body: 'Access is controlled per dealer and role, so each user sees what is relevant to them.',
      },
    ],
  },
  promo: {
    title: 'inFiles is in alpha',
    body: 'The module is currently used internally for testing. Get in touch to request access.',
    cta: { label: 'Request access', href: CONTACT },
  },
  steps: {
    eyebrow: 'How it works',
    heading: 'From job to finished file',
    intro: 'Four steps from request to delivery.',
    panelLabel: 'Illustration of an original file, an ECU chip and a modified file',
    items: [
      { title: 'Create the job', body: 'Pick the vehicle (look it up with the Norwegian Public Roads Administration), tuning type and priority.' },
      { title: 'Upload the original file', body: 'Attach the ECU file from the vehicle, and enter hardware and software.' },
      { title: 'The workshop handles the job', body: 'Follow the status, and talk to the workshop in the chat on the job.' },
      { title: 'Download the modified file', body: 'When the job is done, the modified file is on the job.' },
    ],
  },
  audiences: {
    eyebrow: 'Who it is for',
    heading: 'Built for the whole tuning chain',
    items: [
      {
        icon: 'dealer',
        title: 'Dealer',
        body: 'Create jobs and upload files for your customers’ cars, and follow every job until it is done.',
        link: { label: 'Log in', href: LOGIN },
      },
      {
        icon: 'workshop',
        title: 'Workshop',
        body: 'See incoming jobs with vehicle, files and comments, and deliver modified files back.',
        link: { label: 'Log in', href: LOGIN },
      },
      {
        icon: 'admin',
        title: 'Administrator',
        body: 'Manage users, roles and dealers, and keep an overview of all jobs.',
        link: { label: 'Request access', href: CONTACT },
      },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'Who can use inFiles?',
        answer: 'inFiles is in alpha and is currently used internally for testing. Get in touch to request access.',
      },
      { question: 'How large can the files be?', answer: 'Each file can be up to 50 MB.' },
      {
        question: 'Which file types are supported?',
        answer: 'Original and modified files are uploaded as ordinary files on the job. Get in touch for details on supported formats.',
      },
      {
        question: 'Who can see my files?',
        answer: 'Access is controlled per dealer and role. Users see jobs and files that belong to their dealer.',
      },
    ],
  },
  cta: {
    heading: 'Ready to log in?',
    body: 'Log in to open inFiles, or get in touch to request access.',
    primary: { label: 'Log in', href: LOGIN },
  },
  footer: {
    tagline: 'inFiles — a file service for chip tuning, a module in inSuite.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inFiles',
  },
};

export const INFILES_CONTENT: Record<Locale, InfilesContent> = { no, en };
